import type { LabelLayoutSettingsValues } from "./types"
import type { AlignmentType, TextWrapType, OrientationType } from "./types"

/** Shape of a single printer config from GetPrinterConfigs / GetPrinterConfig */
export interface PrinterConfigApi {
  id: string
  name: string
  isDefault: boolean
  columns: number
  columnGap: number
  labelWidth: number
  labelHeight: number
  marginTop: number
  marginBottom: number
  marginLeft: number
  marginRight: number
  padding: number
  qrCodeSize: number
  qrCodeMarginRight: number
  qrCodeAlignment: string
  productNameFontSize: number
  batchNumberFontSize: number
  expiryFontSize: number
  textAlignment: string
  productNameWrap: string
  productNameMaxLines: number
  zoom: number
  orientation: string
  userId?: string
}

/** Map API printer config to label layout settings (for form/preview). */
export function printerConfigToLabelSettings(api: PrinterConfigApi): LabelLayoutSettingsValues {
  return {
    columns: api.columns,
    columnGap: api.columnGap,
    labelWidth: api.labelWidth,
    labelHeight: api.labelHeight,
    marginTop: api.marginTop,
    marginBottom: api.marginBottom,
    marginLeft: api.marginLeft,
    marginRight: api.marginRight,
    padding: api.padding,
    qrCodeSize: api.qrCodeSize,
    qrCodeMarginRight: api.qrCodeMarginRight,
    qrCodeAlignment: (api.qrCodeAlignment as AlignmentType) || "left",
    productNameFontSize: api.productNameFontSize,
    batchNumberFontSize: api.batchNumberFontSize,
    expiryFontSize: api.expiryFontSize,
    textAlignment: (api.textAlignment as AlignmentType) || "left",
    productNameWrap: (api.productNameWrap as TextWrapType) || "multi",
    productNameMaxLines: api.productNameMaxLines,
    zoom: api.zoom,
    orientation: (api.orientation as OrientationType) || "landscape"
  }
}

/** Map label settings + metadata to CreatePrinterConfig DTO. */
export function labelSettingsToCreateDto(
  settings: LabelLayoutSettingsValues,
  name: string,
  isDefault: boolean
) {
  return {
    name,
    isDefault,
    columns: settings.columns,
    columnGap: settings.columnGap,
    labelWidth: settings.labelWidth,
    labelHeight: settings.labelHeight,
    marginTop: settings.marginTop,
    marginBottom: settings.marginBottom,
    marginLeft: settings.marginLeft,
    marginRight: settings.marginRight,
    padding: settings.padding,
    qrCodeSize: settings.qrCodeSize,
    qrCodeMarginRight: settings.qrCodeMarginRight,
    qrCodeAlignment: settings.qrCodeAlignment,
    productNameFontSize: settings.productNameFontSize,
    batchNumberFontSize: settings.batchNumberFontSize,
    expiryFontSize: settings.expiryFontSize,
    textAlignment: settings.textAlignment,
    productNameWrap: settings.productNameWrap,
    productNameMaxLines: settings.productNameMaxLines,
    zoom: settings.zoom,
    orientation: settings.orientation
  }
}

/** Map label settings + metadata to UpdatePrinterConfig DTO. */
export function labelSettingsToUpdateDto(
  id: string,
  settings: LabelLayoutSettingsValues,
  name: string,
  isDefault: boolean
) {
  return {
    id,
    name,
    isDefault,
    columns: settings.columns,
    columnGap: settings.columnGap,
    labelWidth: settings.labelWidth,
    labelHeight: settings.labelHeight,
    marginTop: settings.marginTop,
    marginBottom: settings.marginBottom,
    marginLeft: settings.marginLeft,
    marginRight: settings.marginRight,
    padding: settings.padding,
    qrCodeSize: settings.qrCodeSize,
    qrCodeMarginRight: settings.qrCodeMarginRight,
    qrCodeAlignment: settings.qrCodeAlignment,
    productNameFontSize: settings.productNameFontSize,
    batchNumberFontSize: settings.batchNumberFontSize,
    expiryFontSize: settings.expiryFontSize,
    textAlignment: settings.textAlignment,
    productNameWrap: settings.productNameWrap,
    productNameMaxLines: settings.productNameMaxLines,
    zoom: settings.zoom,
    orientation: settings.orientation
  }
}
